import axios, { AxiosInstance, AxiosRequestConfig } from "axios";
import Cookies from "js-cookie";
import { IDictionary } from "@common/collections/Dictionary";
import { IApiClient } from "@common/web/api/ApiClient";

declare var appConfig: any;

export default class AxiosClient implements IApiClient {
	instance!: AxiosInstance;

	constructor(settings?: AxiosRequestConfig) {
		this.instance = axios.create(settings|| {});
	}

	protected mergeWithToken(settings?: any): Promise<AxiosRequestConfig> {
		let cfg: AxiosRequestConfig = {
			headers: {
				"Accept-Version": "1",
				"Accept": "application/json",
				"Content-Type": "application/json",
				"Access-Control-Allow-Origin": "*",
				"portal-culture": Cookies.get('Esperantus_Language_languageSwitcher')
			}
		};

		if (settings) cfg = Object.assign(cfg, settings);

		const apiTokenName = appConfig.cookie.apiToken;

		if (apiTokenName) {
			const headers = cfg.headers || {};
			const token = Cookies.get(apiTokenName);
			if (token) headers.authorization = `Basic ${token}`;
			cfg.headers = headers;
		}

		const params = cfg.params || {};
		const cookieNames = apiTokenName
			? Object.keys(appConfig.cookie).filter(e => e !== apiTokenName)
			: Object.keys(appConfig.cookie);

		for (const cookieName of cookieNames) {
			const cookieValue = Cookies.get(appConfig.cookie[cookieName]);
			if (cookieValue) params[cookieName] = cookieValue;
		}

		cfg.params = params;
		return Promise.resolve(cfg);
	}

	request<T = any>(settings: any): Promise<T> {
		return this.mergeWithToken(settings)
			.then(cfg => this.instance.request<T>(cfg))
			.then(res => {
				if (res && res.data && (<any>res.data).hasOwnProperty("$values")) {
					const d = res.data as IDictionary<any>;
					return d["$values"] as T;
				}
				return res.data as T;
			});
	}

	head(url: string, settings?: any): Promise<any> {
		return this.mergeWithToken(settings)
			.then(cfg => this.instance.head(url, cfg));
	}

	get<T = any>(url: string, settings?: any): Promise<T> {
		return this.mergeWithToken(settings)
			.then(cfg => this.instance.get<T>(url, cfg))
			.then(res => {
				if (res && res.data && (<any>res.data).hasOwnProperty("$values")) {
					const d = res.data as IDictionary<any>;
					return d["$values"] as T;
				}
				return res.data as T;
			});
	}

	post<T = any>(url: string, data?: any, settings?: any): Promise<T> {
		return this.mergeWithToken(settings)
			.then(cfg => this.instance.post<T>(url, data, cfg))
			.then(res => {
				if (res && res.data && (<any>res.data).hasOwnProperty("$values")) {
					const d = res.data as IDictionary<any>;
					return d["$values"] as T;
				}
				return res.data as T;
			});
	}

	put<T = any>(url: string, data?: any, settings?: any): Promise<T> {
		return this.mergeWithToken(settings)
			.then(cfg => this.instance.put<T>(url, data, cfg))
			.then(res => {
				if (res && res.data && (<any>res.data).hasOwnProperty("$values")) {
					const d = res.data as IDictionary<any>;
					return d["$values"] as T;
				}
				return res.data as T;
			});
	}

	patch<T = any>(url: string, data?: any, settings?: any): Promise<T> {
		return this.mergeWithToken(settings)
			.then(cfg => this.instance.patch<T>(url, data, cfg))
			.then(res => {
				if (res && res.data && (<any>res.data).hasOwnProperty("$values")) {
					const d = res.data as IDictionary<any>;
					return d["$values"] as T;
				}
				return res.data as T;
			});
	}

	delete(url: string, settings?: any): Promise<any> {
		return this.mergeWithToken(settings)
			.then(cfg => this.instance.delete(url, cfg));
	}
}
